import { useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import axios from "axios";
import { format } from "date-fns";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha
} from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import { SegmentedButtons } from "../components/DocumentActionButtons";
import { DocumentsOrderContact, DocumentsOrderInfo, OrderDocumentDetail } from "../types";

type EmailTemplate = "None" | "E-Commitment" | "E-Confirmation";
type EmailDeliveryType = "Attachment" | "Link";

const emailEndpointMap: Record<EmailTemplate, Record<EmailDeliveryType, string>> = {
  None: {
    Attachment: "/api/orderdocuments/email/fordocuments",
    Link: "/api/orderdocuments/email/fordocumentsaslink"
  },
  "E-Commitment": {
    Attachment: "/api/orderdocuments/email/forcommitmentasattachment",
    Link: "/api/orderdocuments/email/forcommitmentaslink"
  },
  "E-Confirmation": {
    Attachment: "/api/orderdocuments/email/forconfirmationasattachment",
    Link: "/api/orderdocuments/email/forconfirmationaslink"
  }
};

const formatDate = (value: string) => {
  if (!value) {
    return "";
  }

  return format(new Date(value), "MM/dd/yyyy h:mm a");
};

const downloadBase64File = (base64: string, fileName: string, contentType: string) => {
  const link = document.createElement("a");
  link.href = `data:${contentType};base64,${base64}`;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function getContactEmails(contact: DocumentsOrderContact) {
  const splitEmails = contact.emailsSplit?.filter(Boolean) ?? [];
  if (splitEmails.length) {
    return splitEmails;
  }

  return contact.email?.split(";").map((email) => email.trim()).filter(Boolean) ?? [];
}

export function EmailDrawer({
  open,
  orderNumber,
  orderInfo,
  contacts,
  contactsLoading,
  selectedDocuments,
  onRemoveSelectedDocument,
  onClose
}: {
  open: boolean;
  orderNumber: string;
  orderInfo: DocumentsOrderInfo | null;
  contacts: DocumentsOrderContact[];
  contactsLoading: boolean;
  selectedDocuments: OrderDocumentDetail[];
  onRemoveSelectedDocument: (documentId: number) => void;
  onClose: () => void;
}) {
  const [modalDocuments, setModalDocuments] = useState<OrderDocumentDetail[]>([]);
  const [contactsTo, setContactsTo] = useState<string[]>([]);
  const [contactsCc, setContactsCc] = useState<string[]>([]);
  const [template, setTemplate] = useState<EmailTemplate>("None");
  const [deliveryType, setDeliveryType] = useState<EmailDeliveryType>("Attachment");
  const [submittingAction, setSubmittingAction] = useState<"email" | "merge" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selectedDocumentsRef = useRef(selectedDocuments);
  selectedDocumentsRef.current = selectedDocuments;

  const accountRepName = orderInfo?.accountRep?.fullName || orderInfo?.accountRep?.name;
  const contactOptions = useMemo(() => {
    const options = contacts.flatMap((contact, contactIndex) =>
      getContactEmails(contact).map((email, emailIndex) => ({
        key: `${contact.id}-${emailIndex}-${email}`,
        email,
        name: contact.name || email,
        type: contact.type || contact.role || "Contact",
        sortOrder: contact.sortOrder ?? contactIndex
      }))
    );

    if (orderInfo?.accountRep?.email) {
      options.push({
        key: `account-rep-${orderInfo.accountRep.email}`,
        email: orderInfo.accountRep.email,
        name: accountRepName || orderInfo.accountRep.email,
        type: "Account Rep",
        sortOrder: 999
      });
    }

    return options.sort((first, second) => first.sortOrder - second.sortOrder || first.name.localeCompare(second.name));
  }, [accountRepName, contacts, orderInfo?.accountRep]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setModalDocuments(selectedDocumentsRef.current);
    setContactsTo([]);
    setContactsCc([]);
    setTemplate("None");
    setDeliveryType("Attachment");
    setSubmittingAction(null);
    setError(null);
  }, [open, orderNumber]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setModalDocuments((current) => {
      const selectedById = new Map(selectedDocuments.map((document) => [document.id, document]));
      const synchronized = current
        .filter((document) => selectedById.has(document.id))
        .map((document) => selectedById.get(document.id)!);
      const synchronizedIds = new Set(synchronized.map((document) => document.id));

      return [
        ...synchronized,
        ...selectedDocuments.filter((document) => !synchronizedIds.has(document.id))
      ];
    });
  }, [open, selectedDocuments]);

  const handleContactCheck = (email: string, list: "to" | "cc", checked: boolean) => {
    const setPrimary = list === "to" ? setContactsTo : setContactsCc;
    const setOther = list === "to" ? setContactsCc : setContactsTo;

    setPrimary((current) => (checked ? Array.from(new Set([...current, email])) : current.filter((value) => value !== email)));
    if (checked) {
      setOther((current) => current.filter((value) => value !== email));
    }
  };

  const reorderDocuments = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    setModalDocuments((current) => {
      const next = [...current];
      const [document] = next.splice(result.source.index, 1);
      next.splice(result.destination!.index, 0, document);
      return next;
    });
  };

  const removeDocument = (documentId: number) => {
    setModalDocuments((current) => current.filter((document) => document.id !== documentId));
    onRemoveSelectedDocument(documentId);
  };

  const submitEmail = async (merge: boolean) => {
    if (merge && modalDocuments.length === 0) {
      setError("Select at least one document before emailing.");
      return;
    }

    setError(null);
    setSubmittingAction(merge ? "merge" : "email");

    try {
      const firstContact = contacts[0];
      const titleContact = contacts.find((contact) => contact.code === "T");
      const { data } = await axios.post<{ base64Msg: string }>(emailEndpointMap[template][deliveryType], {
        merge,
        documentIds: modalDocuments.map((document) => document.id),
        orderNumber,
        contactEmails: contactsTo,
        contactEmailsCc: contactsCc,
        firstContact: {
          name: firstContact?.name,
          lookupCode: firstContact?.lookupCode
        },
        subject: orderInfo?.emailSubjectLine || `${orderNumber}-email`,
        titleUrl: titleContact?.url,
        stateCode: orderInfo?.stateCode || modalDocuments[0]?.mtaNum?.substring(3, 5),
        transactionType: orderInfo?.transactionType
      });

      downloadBase64File(data.base64Msg, `${orderInfo?.emailSubjectLine || `${orderNumber}-email`}.msg`, "application/vnd.ms-outlook");
      onClose();
    } catch {
      setError("The email could not be generated.");
    } finally {
      setSubmittingAction(null);
    }
  };

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={open}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "min(860px, calc(100vw - 360px))" },
          maxWidth: "100%"
        }
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider"
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Email
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {orderNumber}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: { xs: "auto", lg: "hidden" },
            p: 2
          }}
        >
          <Stack spacing={2} sx={{ height: { lg: "100%" }, minHeight: 0 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(380px, 420px)" },
                gap: 2,
                flex: 1,
                minHeight: 0
              }}
            >
              <Stack spacing={1} sx={{ alignSelf: "start", minWidth: 0 }}>
                <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      Selected Documents
                    </Typography>
                    <Box
                      sx={{
                        display: { xs: "none", md: "inline-flex" },
                        minWidth: 22,
                        height: 22,
                        px: 0.75,
                        borderRadius: 1,
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "primary.dark",
                        color: "common.white",
                        fontSize: 13,
                        fontWeight: 900
                      }}
                    >
                      {modalDocuments.length}
                    </Box>
                  </Stack>
                  {modalDocuments.length > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      Drag to reorder
                    </Typography>
                  )}
                </Stack>
                {modalDocuments.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      minHeight: 120,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 1,
                      backgroundColor: "background.neutral"
                    }}
                  >
                    <Typography sx={{ color: "primary.dark", fontWeight: 800, textAlign: "center" }}>
                      Please select documents
                    </Typography>
                  </Paper>
                ) : (
                  <DragDropContext onDragEnd={reorderDocuments}>
                    <Droppable droppableId="email-selected-documents">
                      {(provided) => (
                        <Stack ref={provided.innerRef} {...provided.droppableProps} spacing={1}>
                          {modalDocuments.map((document, index) => (
                            <Draggable key={document.id} draggableId={`email-document-${document.id}`} index={index}>
                              {(dragProvided, snapshot) => (
                                <Paper
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  variant="outlined"
                                  sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    backgroundColor: (theme) =>
                                      snapshot.isDragging
                                        ? alpha(theme.palette.primary.main, 0.08)
                                        : theme.palette.background.paper,
                                    boxShadow: snapshot.isDragging ? "0 8px 24px rgba(22, 28, 36, 0.16)" : "none",
                                    ...dragProvided.draggableProps.style
                                  }}
                                >
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <IconButton
                                      size="small"
                                      {...dragProvided.dragHandleProps}
                                      sx={{
                                        color: "text.secondary",
                                        cursor: snapshot.isDragging ? "grabbing" : "grab",
                                        flexShrink: 0
                                      }}
                                    >
                                      <Iconify icon="eva:menu-2-fill" width={18} />
                                    </IconButton>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        display: { xs: "none", md: "block" },
                                        width: 20,
                                        flexShrink: 0,
                                        color: "#ff4842",
                                        fontWeight: 800,
                                        textAlign: "center"
                                      }}
                                    >
                                      {index + 1}
                                    </Typography>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography sx={{ color: "primary.dark", fontWeight: 800 }} noWrap>
                                        {document.documentDescription || "Untitled document"}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {formatDate(document.date1)}
                                      </Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => removeDocument(document.id)}>
                                      <Iconify icon="eva:trash-2-outline" />
                                    </IconButton>
                                  </Stack>
                                </Paper>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Stack>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </Stack>

              <Stack spacing={2} sx={{ minHeight: 0, overflow: { lg: "hidden" } }}>
                <Box sx={{ display: "flex", flex: 1, minHeight: 0, flexDirection: "column" }}>
                  <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      Email Contacts
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {contactOptions.length} available
                    </Typography>
                  </Stack>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 1,
                      overflow: "hidden",
                      backgroundColor: "background.paper",
                      display: "flex",
                      flex: 1,
                      minHeight: 0,
                      flexDirection: "column"
                    }}
                  >
                    <Box
                      sx={{
                        px: 1,
                        py: 0.6,
                        display: "grid",
                        gridTemplateColumns: "34px 34px minmax(0, 1fr)",
                        gap: 0.5,
                        backgroundColor: "grey.100",
                        alignItems: "center"
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 900, color: "text.secondary", textAlign: "center", textTransform: "uppercase" }}
                      >
                        To
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 900, color: "text.secondary", textAlign: "center", textTransform: "uppercase" }}
                      >
                        Cc
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary", textTransform: "uppercase" }}>
                        Contact
                      </Typography>
                    </Box>
                    {contactsLoading ? (
                      <Stack alignItems="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </Stack>
                    ) : contactOptions.length === 0 ? (
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          No email contacts found.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ flex: 1, minHeight: 0, overflowY: { xs: "visible", lg: "auto" } }}>
                        {contactOptions.map((contact) => (
                          <Box
                            key={contact.key}
                            sx={{
                              px: 1,
                              py: 0.75,
                              display: "grid",
                              gridTemplateColumns: "34px 34px minmax(0, 1fr)",
                              gap: 0.5,
                              alignItems: "center",
                              borderTop: "1px solid",
                              borderColor: "divider",
                              "&:hover": {
                                backgroundColor: "action.hover"
                              }
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={contactsTo.includes(contact.email)}
                              onChange={(_, checked) => handleContactCheck(contact.email, "to", checked)}
                              sx={{ p: 0.25, justifySelf: "center" }}
                            />
                            <Checkbox
                              size="small"
                              checked={contactsCc.includes(contact.email)}
                              onChange={(_, checked) => handleContactCheck(contact.email, "cc", checked)}
                              sx={{ p: 0.25, justifySelf: "center" }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography title={contact.name} sx={{ fontSize: 14, fontWeight: 850, lineHeight: 1.25 }} noWrap>
                                {contact.name}
                              </Typography>
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  title={contact.type}
                                  sx={{ color: "text.secondary", maxWidth: "42%", flexShrink: 0 }}
                                  noWrap
                                >
                                  {contact.type}
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                  -
                                </Typography>
                                <Typography
                                  variant="caption"
                                  title={contact.email}
                                  sx={{ color: "text.secondary", minWidth: 0 }}
                                  noWrap
                                >
                                  {contact.email}
                                </Typography>
                              </Stack>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                    Email Details
                  </Typography>
                  <Stack spacing={1}>
                    <TextField
                      select
                      label="Email Template"
                      size="small"
                      value={template}
                      onChange={(event) => setTemplate(event.target.value as EmailTemplate)}
                    >
                      {(["None", "E-Commitment", "E-Confirmation"] as EmailTemplate[]).map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                    <SegmentedButtons
                      value={deliveryType}
                      options={[
                        { value: "Attachment", label: "Attachment" },
                        { value: "Link", label: "Link" }
                      ]}
                      onChange={(value) => setDeliveryType(value as EmailDeliveryType)}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" variant="outlined" onClick={onClose} sx={{ borderRadius: 1, fontWeight: 800, textTransform: "none" }}>
              Cancel
            </Button>
            <Button size="small"
              variant="contained"
              disabled={submittingAction !== null}
              onClick={() => submitEmail(false)}
              sx={{ borderRadius: 1, fontWeight: 900, textTransform: "none" }}
            >
              {submittingAction === "email" ? "Generating..." : "Email"}
            </Button>
            <Button size="small"
              variant="contained"
              disabled={submittingAction !== null || modalDocuments.length === 0}
              onClick={() => submitEmail(true)}
              sx={{ borderRadius: 1, fontWeight: 900, textTransform: "none" }}
            >
              {submittingAction === "merge" ? "Generating..." : "Merge And Email"}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
}
